import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Rating,
  Link,
  Chip,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Button,
  InputAdornment,
} from "@mui/material";
import { ReviewDetail } from "../types/Review";
import { getReviewsByUser } from "../services/api";
import LoadingPlaceholder from "./LoadingPlaceholder";
import Pagination from "@mui/material/Pagination";
import { Link as RouterLink, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AdvertisementResponsive from "../components/advertisement/Ad_GA_res";
import { formatReviewText } from "../utils/formatText";
import { useTheme, useMediaQuery } from "@mui/material";
import { debounce } from 'lodash';
import { SelectChangeEvent } from '@mui/material';
import SortIcon from "@mui/icons-material/Sort";
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Clear from '@mui/icons-material/Clear';
import { likeReview, unlikeReview } from '../services/api';
import DetailScores from './review/DetailScores';
import { scoreTags } from '../utils/scoreTags';

interface ReviewsTabProps {
  username: string;
  isPublic?: boolean;
}

const ReviewsTab: React.FC<ReviewsTabProps> = ({ username, isPublic = false }) => {
  const [reviews, setReviews] = useState<ReviewDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const maxLength = {
    xs: 180, // スマートフォン向け
    sm: 320  // タブレット/デスクトップ向け
  };
  const maxLines = 8;


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 検索クエリの変更を遅延させる
  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      setPage(1); // ページ状態を1に戻す
      setSearchParams(params => {
        params.set('page', '1');
        if (query) params.set('query', query);
        else params.delete('query');
        return params;
      });
    }, 500),
    [setSearchParams]
  );

  // 並び替えの変更をハンドル
  const handleSortChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setSortOrder(newOrder);
    setPage(1); // ページ状態を1に戻す
    setSearchParams(params => {
      params.set('page', '1');
      if (newOrder !== 'default') params.set('order', newOrder);
      else params.delete('order');
      return params;
    });
  };

  // フィルターをクリア
  const handleClearFilter = () => {
    setSearchQuery('');
    setSortOrder('default');
    setPage(1); // ページ状態を1に戻す
    setSearchParams(params => {
      params.set('page', '1');
      params.delete('query');
      params.delete('order');
      return params;
    });
  };

  // ページ変更のハンドラー
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    setSearchParams(params => {
      params.set('page', value.toString());
      return params;
    });
  };

  // 並び替えの表示テキストをマッピング
  const orderDisplayText: { [key: string]: string } = {
    'default': '投稿日時 (新しい順)',
    'added_date_asc': '投稿日時 (古い順)',
    'score_desc': '評価 (高い順)',
    'score_asc': '評価 (低い順)',
    'release_date_desc': '発売日 (新しい順)',
    'release_date_asc': '発売日 (古い順)',
    'update_date_desc': '更新日時 (新しい順)',
    'update_date_asc': '更新日時 (古い順)'
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await getReviewsByUser(
          username, 
          page,
          searchParams.get('query') || '',
          searchParams.get('order') || 'default'
        );
        setReviews(response.results);
        setTotalCount(response.count);
        setTotalPages(Math.ceil(response.count / 10));
      } catch (error) {
        console.error("レビューの取得に失敗しました:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [username, page, searchParams]);

  const toggleExpand = (reviewId: number) => {
    setExpanded((prev) => [...prev, reviewId]);
  };

  const formatText = (text: string) => {
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const truncateText = (text: string) => {
    const lines = text.split("\n");
    const currentMaxLength = isMobile ? maxLength.xs : maxLength.sm;

    if (lines.length > maxLines || text.length > currentMaxLength) {
      const truncatedLines = lines.slice(0, maxLines);
      let truncatedText = truncatedLines.join("\n");
      if (truncatedText.length > currentMaxLength) {
        truncatedText = truncatedText.slice(0, currentMaxLength);
      }
      return `${truncatedText}...`;
    }
    return text;
  };

  const getCoverUrl = (game: any) => {
    const coverId = game.cover_id;
    return coverId
      ? coverId.startsWith('pulse_')
        ? `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`
        : `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`
      : "/image/no_image.png"; // フォールバック用の画像パス
  };

  const formatPlayDate = (date: string | null) => {
    if (date === null) return "";
    const playDate = new Date(date);
    return playDate.toLocaleDateString();
  };

  // スコアに応じて色を取得する関数を追加
  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666"; // スコアがない場合は灰色を返す
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const navigate = useNavigate();
  const location = useLocation();

  const ReviewContent: React.FC<{
    review: ReviewDetail;
    isExpanded: boolean;
    onExpand: (reviewId: number) => void;
  }> = ({ review, isExpanded, onExpand }) => {
    const [isLiked, setIsLiked] = useState(review.is_liked);
    const [likeCount, setLikeCount] = useState(review.like_count);
    const [userLikeCount, setUserLikeCount] = useState(review.user_like_count || 0);

    const handleLikeClick = async () => {
      if (!localStorage.getItem('token')) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }

      try {
        if (userLikeCount >= 10) {
          console.error('いいねの上限（10回）に達しています。');
          return;
        }
        const response = await likeReview(review.id);
        setIsLiked(true);
        setLikeCount(response.data.count);
        setUserLikeCount(response.data.like_count);
      } catch (error: any) {
        console.error('イイね処理に失敗しました', error);
        if (error?.response?.data?.error) {
          console.error(error.response.data.error);
        }
      }
    };

    const displayText = isExpanded
      ? review.review_text
      : truncateText(review.review_text);
    
    const currentMaxLength = isMobile ? maxLength.xs : maxLength.sm;
    const shouldShowReadMore = !isExpanded && (
      review.review_text.length > currentMaxLength ||
      review.review_text.split('\n').length > maxLines
    );

    return (
      <Box>
        <Box sx={{ 
          position: "relative", 
          mt: 1, 
          mb: 0,
          '& .spoiler': {
            backgroundColor: '#444',
            padding: '0 4px',
            borderRadius: '2px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }
        }}>
          <Typography variant="body2" component="div" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: { xs: 1.3, sm: 1.3 } }}>
            {formatReviewText(displayText, isMobile)}
          </Typography>
          {shouldShowReadMore && (
            <Link
              component="button"
              onClick={() => onExpand(review.id)}
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                cursor: "pointer",
                bgcolor: "background.default",
                pl: 1,
                fontSize: "0.7rem",
              }}
            >
              全て読む
            </Link>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 
          }}>
            {review.review_text && (
              <Button
                onClick={handleLikeClick}
                startIcon={isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                color={isLiked ? "primary" : "inherit"}
                disabled={userLikeCount >= 10}
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.25,
                  fontSize: '0.75rem',
                  borderRadius: 1,
                  lineHeight: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 1,
                    '& svg': {
                      fontSize: '0.8rem'
                    }
                  },
                  '&.Mui-disabled': {
                    opacity: 0.7,
                    color: theme.palette.primary.main
                  }
                }}
              >
                <Typography variant="caption" color="text.primary" lineHeight={1}>
                  {likeCount}
                  {userLikeCount > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 'bold'
                      }}
                    >
                      {` (+${userLikeCount})`}
                    </Typography>
                  )}
                </Typography>
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" lineHeight={1}>
              {new Date(review.created_at).toLocaleDateString()}に投稿
            </Typography>
          </Box>
          <Link
            component={RouterLink}
            to={`/game/${review.game.id}/reviews/${review.id}`}
            sx={{
              display: "flex",
              alignItems: "center",
              color: "pulse.info",
              textDecoration: "none",
              fontSize: "0.75rem",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: "1rem", ml: 0.5 }} />
          </Link>
        </Box>
      </Box>
    );
  };

  // レビュー用のローディングプレースホルダーコンポーネント
  const ReviewLoadingPlaceholder = () => {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={120} height={24} />
            <Skeleton variant="text" width={80} height={20} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="text" width={100} height={24} />
        </Box>
      </Box>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={8.5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="Search..."
              variant="standard"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                debouncedSearch(e.target.value);
              }}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-input': {
                  ...theme.typography.body2,
                },
                '& .MuiInputBase-input::placeholder': {
                  opacity: 0.5
                },
                '& .MuiInputBase-root:before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInputBase-root:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                displayEmpty
                sx={{
                  minWidth: 100,
                  ...theme.typography.body2,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  '& .MuiSelect-select': {
                    paddingY: 0.5,
                    paddingX: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  }
                }}
                                 renderValue={(selected) => {
                   const selectedOption = Object.entries(orderDisplayText).find(([key]) => key === selected);
                   return (
                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                       <SortIcon sx={{ 
                         mr: 0.5, 
                         fontSize: '1rem', 
                         display: { xs: 'none', sm: 'block' } 
                       }} />
                       {selectedOption ? selectedOption[1] : 'デフォルト'}
                     </Box>
                   );
                 }}
              >
                <MenuItem value="default" dense>投稿日時 (新しい順)</MenuItem>
                <MenuItem value="added_date_asc" dense>投稿日時 (古い順)</MenuItem>
                <MenuItem value="score_desc" dense>評価 (高い順)</MenuItem>
                <MenuItem value="score_asc" dense>評価 (低い順)</MenuItem>
                <MenuItem value="release_date_desc" dense>発売日 (新しい順)</MenuItem>
                <MenuItem value="release_date_asc" dense>発売日 (古い順)</MenuItem>
                <MenuItem value="update_date_desc" dense>更新日時 (新しい順)</MenuItem>
                <MenuItem value="update_date_asc" dense>更新日時 (古い順)</MenuItem>
              </Select>
            </Box>
          </Box>
                                {searchQuery !== '' && (
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                 {totalCount}件のレビューが見つかりました
               </Typography>
               <Button
                 size="small"
                 onClick={handleClearFilter}
                 startIcon={<Clear fontSize="small" />}
                 sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
               >
                 Clear filter
               </Button>
             </Box>
           )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={12} md={8.5}>
        <Box>
          {loading ? (
            <Box>
              {[...Array(3)].map((_, index) => (
                <ReviewLoadingPlaceholder key={index} />
              ))}
            </Box>
          ) : (
            <>
              {reviews.length === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      まだレビューがありません
                    </Typography>
                    {!isPublic && (
                      <Typography variant="body1" color="text.secondary">
                        ゲームをプレイしたら、レビューを書いてみましょう
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ) : (
                <>
                  {reviews.map((review) => {
                    const isExpanded = expanded.includes(review.id);
                    const displayText = isExpanded
                      ? review.review_text
                      : truncateText(review.review_text);

                    return (
                      <Box
                        key={review.id}
                        sx={{
                          mb: 2,
                          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                          "&:hover": {
                            transform: "none",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            paddingBottom: { xs: 1, sm: 1.5 },
                            paddingTop: { xs: 1, sm: 1.5 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              component={RouterLink}
                              to={`/game/${review.game.id}`}
                              sx={{
                                display: 'block',
                                width: "20%",
                                maxWidth: 120,
                                mr: 1.5,
                              }}
                            >
                              <Box
                                component="img"
                                src={getCoverUrl(review.game)}
                                alt={review.game.name}
                                sx={{
                                  width: "100%",
                                  borderRadius: 1,
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Link
                                  component={RouterLink}
                                  to={`/game/${review.game.id}`}
                                  color="inherit"
                                  underline="hover"
                                  sx={{
                                    typography: "h6",
                                    display: "block",
                                    mb: 0.5,
                                    fontSize: {
                                      xs: "0.75rem!important",
                                      sm: "0.9rem!important",
                                    },
                                  }}
                                >
                                  {review.game.name}
                                </Link>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mb: 1,
                                  flexDirection: { xs: "column", sm: "row" },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                        py: 0.25,
                                        px: 0.75,
                                        lineHeight: "1",
                                        borderRadius: 1,
                                        backgroundColor: `${getScoreColor(
                                          review.score
                                        )}B3`,
                                      }}
                                    >
                                      {review.score !== null
                                        ? review.score.toFixed(1)
                                        : "-"}
                                    </Typography>
                                    <Box
                                      sx={{
                                        width: 80,
                                        height: 4,
                                        bgcolor: "rgba(255, 255, 255, 0.3)",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width:
                                            review.score !== null
                                              ? `${((review.score - 1) / 4) * 100}%`
                                              : "100%",
                                          height: "100%",
                                          bgcolor: getScoreColor(review.score),
                                          borderRadius: 2,
                                          transition: "width 0.3s ease",
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {review.play_time && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "pulse.info",
                                      }}
                                    >
                                      <TimerOutlinedIcon
                                        fontSize="inherit"
                                        sx={{ mr: 0.5, fontSize: "0.85rem" }}
                                      />
                                      {review.play_time}時間
                                    </Typography>
                                  )}
                                  {!review.play_time && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "pulse.info",
                                      }}
                                    >
                                      <TimerOutlinedIcon
                                        fontSize="inherit"
                                        sx={{ mr: 0.5, fontSize: "0.85rem" }}
                                      />
                                      -
                                    </Typography>
                                  )}
                                  {review.play_start_date && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "pulse.info",
                                      }}
                                    >
                                      <PlayCircleOutlineIcon
                                        fontSize="inherit"
                                        sx={{ mr: 0.5, fontSize: "0.85rem" }}
                                      />
                                      {formatPlayDate(review.play_start_date)}
                                      {review.play_end_date && " - "}
                                      {formatPlayDate(review.play_end_date)}
                                    </Typography>
                                  )}
                                  {!review.play_start_date && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "pulse.info",
                                      }}
                                    >
                                      <PlayCircleOutlineIcon
                                        fontSize="inherit"
                                        sx={{ mr: 0.5, fontSize: "0.85rem" }}
                                      />
                                      -
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {review.score_tags &&
                                review.score_tags.map((tagId) => {
                                  const tag = scoreTags.find((t) => t.id === tagId);
                                  return (
                                    tag && (
                                      <Chip
                                        key={tag.id}
                                        icon={
                                          tag.id === 9 || tag.id >= 30 ? (
                                            <KeyboardDoubleArrowDownIcon
                                              style={{ color: "#79bcf3" }}
                                            />
                                          ) : (
                                            <KeyboardDoubleArrowUpIcon
                                              style={{ color: "#f37979" }}
                                            />
                                          )
                                        }
                                        label={tag.label}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          mr: 0.5,
                                          mb: 0.5,
                                          height: "1.1rem",
                                          "& .MuiChip-icon": { fontSize: "0.7rem" },
                                          "& .MuiChip-label": {
                                            fontSize: "0.6rem",
                                            py: 0.25,
                                          },
                                        }}
                                      />
                                    )
                                  );
                                })}
                              <DetailScores
                                scoreVisual={review.score_visual}
                                scoreSound={review.score_sound}
                                scoreGameplay={review.score_gameplay}
                                scoreNarrative={review.score_narrative}
                                scoreOriginality={review.score_originality}
                                variant="reviewsTab"
                              />
                              <ReviewContent
                                review={review}
                                isExpanded={isExpanded}
                                onExpand={toggleExpand}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                  {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={3.5} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ position: 'sticky', top: 80 }}>
          <AdvertisementResponsive />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ReviewsTab;
